/**
 * Apryse keeps three things for pages it has drawn and, in normal viewing,
 * lets go of them only when the document closes: the on-screen annotation
 * layer, a hidden canvas it draws annotations into, and the page's raw
 * pixels. On a several-thousand-page record that outgrows the browser, after
 * which the viewer paints pages white without raising an error.
 *
 * This frees them once a page is off screen. Apryse re-sizes and redraws
 * freed canvases itself when the page comes back.
 */

const PAGE_LAYER_SELECTOR = "canvas.auxiliary";

export const RELEASE_DELAY_MS = 1000;

// The hidden canvases and raw pixels are reachable only through minified
// internals that Apryse renames in every build. On any other build only the
// on-screen layers are freed, until these names are re-checked against it.
export const SUPPORTED_APRYSE_BUILD = "Mi8xOC8yMDI2fDdhNmFkMTdjODQ=";

let warnedUnsupportedBuild = false;

function offscreenPageReleaser(core, documentViewer) {
  let build = null;
  try {
    build = core?.getBuild?.();
  } catch {
    build = null;
  }
  if (build !== SUPPORTED_APRYSE_BUILD) {
    if (!warnedUnsupportedBuild) {
      warnedUnsupportedBuild = true;
      console.warn(
        "Viewer build changed: off-screen pages keep their hidden canvases and pixels until releaseOffscreenPageMemory is re-checked.",
      );
    }
    return null;
  }

  const renderer = documentViewer.yb;
  const usable =
    renderer &&
    renderer.xd &&
    typeof renderer.xd === "object" &&
    Array.isArray(renderer.og) &&
    ["X_a", "pG", "VOa"].every((name) => typeof renderer[name] === "function");
  if (!usable) return null;

  return () => {
    const onScreen = new Set(renderer.og);
    Object.keys(renderer.xd).forEach((pageId) => {
      const pageIndex = renderer.VOa(pageId);
      if (pageIndex < 0 || onScreen.has(pageIndex)) return;
      renderer.X_a(pageId);
      renderer.pG(pageIndex, documentViewer.id);
    });
  };
}

export function releaseOffscreenPageMemory(
  instance,
  { delayMs = RELEASE_DELAY_MS } = {},
) {
  const core = instance?.Core;
  const documentViewer = core?.documentViewer;
  if (!documentViewer || typeof MutationObserver === "undefined") {
    return () => {};
  }

  const pending = new Set();
  let timer = null;
  let observedRoot = null;

  const release = () => {
    timer = null;
    pending.forEach((layer) => {
      if (!layer.isConnected && layer.width) {
        layer.width = 0;
        layer.height = 0;
      }
    });
    pending.clear();
    try {
      offscreenPageReleaser(core, documentViewer)?.();
    } catch (error) {
      console.warn("Could not release off-screen page memory:", error);
    }
  };

  const observer = new MutationObserver((records) => {
    let pageLeft = false;
    records.forEach((record) => {
      record.removedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        pageLeft = true;
        if (node.matches(PAGE_LAYER_SELECTOR)) pending.add(node);
        node.querySelectorAll(PAGE_LAYER_SELECTOR).forEach((layer) => pending.add(layer));
      });
    });
    if (pageLeft && !timer) {
      timer = setTimeout(release, delayMs);
    }
  });

  const observeScrollView = () => {
    const root = documentViewer.getScrollViewElement?.();
    if (!root || root === observedRoot) return;
    observer.disconnect();
    observer.observe(root, { childList: true, subtree: true });
    observedRoot = root;
  };

  observeScrollView();
  documentViewer.addEventListener?.("documentLoaded", observeScrollView);

  return () => {
    documentViewer.removeEventListener?.("documentLoaded", observeScrollView);
    observer.disconnect();
    clearTimeout(timer);
    pending.clear();
  };
}
